import { useState } from 'react'
import { Download, Search } from 'lucide-react'
import { Button, Pagination } from '../../../../components'
import { AVATAR_COLOR_CLASSES } from '../constants/hrmsAttendanceLeave.constants'
import type { DropdownOption, PendingRequest } from '../types/hrmsAttendanceLeave.types'
import { RequestTypeChip } from './RequestTypeChip'

interface PendingRequestsCardProps {
  requests: PendingRequest[]
  total?: number
  page?: number
  limit?: number
  loading?: boolean
  search?: string
  onSearchChange?: (search: string) => void
  departmentId?: string
  onDepartmentChange?: (dept: string) => void
  departmentOptions?: DropdownOption[]
  requestType?: string
  onRequestTypeChange?: (type: string) => void
  onPageChange?: (page: number) => void
  onViewAll?: () => void
  onReviewRequest: (id: string) => void
  onExportDailySheet?: () => void
}

const REQUEST_TYPE_OPTIONS = [
  { label: 'All Request Types', value: 'ALL' },
  { label: 'Leave Request', value: 'LEAVE_REQUEST' },
  { label: 'Regularization', value: 'REGULARIZATION' },
]

function getInitials(name: string): string {
  const parts = name.trim().split(' ')
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

function formatSubmittedTime(submittedAt?: string): string {
  if (!submittedAt) return '—'
  try {
    const subDate = new Date(submittedAt)
    if (isNaN(subDate.getTime())) return submittedAt

    const now = new Date()
    const diffMs = now.getTime() - subDate.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffHours < 1) return 'Just now'
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`

    return subDate.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return submittedAt
  }
}

export function PendingRequestsCard({
  requests,
  total,
  page,
  limit,
  loading = false,
  search = '',
  onSearchChange,
  departmentId = '',
  onDepartmentChange,
  departmentOptions = [],
  requestType = 'ALL',
  onRequestTypeChange,
  onPageChange,
  onReviewRequest,
  onExportDailySheet,
}: PendingRequestsCardProps) {
  const [localPage, setLocalPage] = useState<number>(1)
  const pageSize = limit || 5
  const isControlled = typeof page === 'number' && typeof total === 'number'

  const effectivePage = isControlled ? page : localPage
  const effectiveTotal = isControlled ? total : requests.length
  const totalPages = Math.ceil(effectiveTotal / pageSize) || 1
  const safePage = Math.min(effectivePage, totalPages)

  const paginatedRequests = isControlled
    ? requests
    : requests.slice((safePage - 1) * pageSize, safePage * pageSize)

  const from = effectiveTotal === 0 ? 0 : (safePage - 1) * pageSize + 1
  const to = Math.min(safePage * pageSize, effectiveTotal)

  const handlePageSelect = (newPage: number) => {
    if (onPageChange) {
      onPageChange(newPage)
    } else {
      setLocalPage(newPage)
    }
  }

  const handleExport = () => {
    if (onExportDailySheet) {
      onExportDailySheet()
      return
    }
    const headers = ['Employee', 'Department', 'Request Type', 'Title', 'Detail', 'Submitted']
    const escapeCsv = (val: string) => `"${val.replace(/"/g, '""')}"`
    const rows = requests.map((r) => [
      escapeCsv(r.name),
      escapeCsv(r.department),
      escapeCsv(r.requestType),
      escapeCsv(r.title),
      escapeCsv(r.detail),
      escapeCsv(formatSubmittedTime(r.submittedAt)),
    ])
    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', `Pending_Requests_Sheet.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-xs min-w-0 space-y-4">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 min-w-0">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-bold text-slate-900">Pending Requests Queue</h2>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
              {effectiveTotal} {effectiveTotal === 1 ? 'action required' : 'actions required'}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-0.5">
            Requests that require HR authorization or manager regularization approval.
          </p>
        </div>

        <div className="flex items-center gap-2.5 shrink-0 self-start sm:self-center">
          <Button
            variant="secondary"
            size="sm"
            icon={<Download className="h-4 w-4" />}
            onClick={handleExport}
            className="bg-white hover:bg-slate-50 text-slate-700 border-slate-200 h-9 text-xs rounded-lg font-semibold shadow-xs whitespace-nowrap"
          >
            Export Requests
          </Button>
        </div>
      </div>

      {/* Filter Bar: Search, Department, Request Type */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-1">
        {/* Search */}
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder="Search by employee name or title..."
            className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-xs text-slate-800 placeholder-slate-400 focus:border-[#146971] focus:outline-none"
          />
        </div>

        {/* Department Filter */}
        {departmentOptions.length > 0 && (
          <div className="w-full">
            <select
              value={departmentId}
              onChange={(e) => onDepartmentChange?.(e.target.value)}
              className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 focus:border-[#146971] focus:outline-none cursor-pointer"
            >
              {departmentOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Request Type Filter */}
        <div className="w-full">
          <select
            value={requestType}
            onChange={(e) => onRequestTypeChange?.(e.target.value)}
            className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 focus:border-[#146971] focus:outline-none cursor-pointer"
          >
            {REQUEST_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="w-full overflow-x-auto min-w-0 rounded-xl border border-slate-200">
        <table className="w-full min-w-[700px] text-left text-sm border-collapse">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs uppercase font-semibold border-b border-slate-200">
              <th className="py-3 px-4">EMPLOYEE</th>
              <th className="py-3 px-4">REQUEST TYPE</th>
              <th className="py-3 px-4">DETAILS</th>
              <th className="py-3 px-4">SUBMITTED</th>
              <th className="py-3 px-4">STATUS</th>
              <th className="py-3 px-4 text-right">ACTION</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={6} className="py-12 text-center text-slate-500">
                  <div className="inline-flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#146971] border-t-transparent" />
                    <span>Loading pending requests...</span>
                  </div>
                </td>
              </tr>
            ) : paginatedRequests.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-12 text-center text-slate-500">
                  No pending requests matching the criteria
                </td>
              </tr>
            ) : (
              paginatedRequests.map((req, index) => {
                const colorClass = AVATAR_COLOR_CLASSES[index % AVATAR_COLOR_CLASSES.length]

                return (
                  <tr key={req.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-9 h-9 rounded-full border flex items-center justify-center font-bold text-xs shrink-0 ${colorClass}`}
                        >
                          {getInitials(req.name)}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">{req.name}</div>
                          <div className="text-xs text-slate-500">{req.department}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <RequestTypeChip requestType={req.requestType} />
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-semibold text-slate-900">{req.title}</div>
                      <div className="text-xs text-slate-500">{req.detail}</div>
                    </td>
                    <td className="py-3 px-4 text-slate-700 font-medium">
                      {formatSubmittedTime(req.submittedAt)}
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 border border-amber-200">
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                        Pending
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => onReviewRequest(req.id)}
                        className="h-7 px-3 text-xs"
                      >
                        Review
                      </Button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pt-2">
          <Pagination
            summary={
              <span>
                Showing <span className="font-bold">{from}</span>–<span className="font-bold">{to}</span> of{' '}
                <span className="font-bold">{effectiveTotal}</span> requests
              </span>
            }
            currentPage={safePage}
            totalPages={totalPages}
            previousDisabled={safePage === 1}
            nextDisabled={safePage === totalPages || totalPages === 0}
            onPrevious={() => handlePageSelect(Math.max(1, safePage - 1))}
            onNext={() => handlePageSelect(Math.min(totalPages, safePage + 1))}
            onPageChange={handlePageSelect}
          />
        </div>
      )}
    </div>
  )
}
